// CANONICAL COPY (MNSA-01, the GH #7115 pattern). src/utils/obligationEvaluations.js re-exports this file so the browser and the
// Function App share one definition. Edit here, never there.
'use strict';

/**
 * src/utils/obligationEvaluations.js
 *
 * The browser's view of the obligation evaluation cycle (CTX-04; msr 4.5b).
 * The rule is azure-functions/shared/obligationEvaluations/cycle.cjs — the one
 * CTX-04 and obligation-evaluations-write read — re-exported here, never
 * copied, so the screen and the metric cannot disagree.
 *
 * buildEvaluationTasks() turns "this obligation's evaluation falls due" into a
 * Tier 1 obligation-derived task (docs/task-system-redesign.md): computed on
 * the fly from domain state, never stored, gone the moment an evaluation that
 * counts is recorded, and back when the next cycle falls due. It joins the one
 * obligation task list (useAppState, useObligationTasks) — there is no second
 * task list.
 */

const { OBLIGATION_EVALUATION_FROM, OUTCOMES, obligationCycleState, evaluationsOf, creditRefusal, findingFor, evidenceResolves, isVoided } = require('./cycle.cjs');
const { OBLIGATIONS_BY_ID } = require('../compliance/obligations/index.cjs');
const { realEmployees } = require('../sampleRecords.cjs');
const { getEquivalentControls } = require('../compliance/controlMappings.cjs');
const { deriveResponsibility } = require('../obligationResponsibility.cjs');


const EVALUATION_TASK_KIND = 'obligation-evaluation';

const OUTCOME_LABELS = Object.freeze({
  'no-change-needed': 'No change needed',
  'change-needed': 'Change needed',
});

/** Why an evaluation earns no credit, in words a compliance lead reads. */
const REFUSAL_LABELS = Object.freeze({
  voided: 'withdrawn',
  noEvaluator: 'names no evaluator',
  unknownOutcome: 'has no outcome',
  evidenceUnresolved: 'its evidence no longer exists',
  changeNeededNoFinding: 'no finding has been raised for the change it calls for',
  findingsWithheld:
    'the findings register is not in your plan, so the change it calls for cannot be tracked',
});

const EDIT_ROLES = new Set(['admin', 'manager']);

/**
 * May this signed-in person record an evaluation of the obligation? The same
 * rule obligation-evaluations-write applies: an admin or manager, the explicit
 * assignee, or an owner of its mapped controls (COP-B-004) — never a
 * super-admin. With no user list the control-owner path cannot be derived, so
 * the answer is the stricter one.
 */
function mayEvaluateObligation(state, obligationId, session, users) {
  if (!session || session.role === 'super-admin' || !session.userId) return false;
  if (EDIT_ROLES.has(session.role)) return true;
  const entry = (state?.enabledObligations || []).find(
    (e) => e && e.obligationId === obligationId && e.enabled !== false
  );
  const def = OBLIGATIONS_BY_ID.get(obligationId);
  if (!entry || !def) return false;
  if (entry.assigneeId && entry.assigneeId === session.userId) return true;
  if (!Array.isArray(users)) return false;
  const controlIds = [...new Set((def.scfIds || []).flatMap((id) => getEquivalentControls(id)))];
  const r = deriveResponsibility({ enabledObligation: entry, controlIds, state, users });
  const ids = new Set(r.responsibleEmployeeIds || []);
  return realEmployees(state?.employees || []).some(
    (e) => e && ids.has(e.id) && e.linkedUserId === session.userId
  );
}

/** May this person void (or raise the finding of) this evaluation? */
function mayVoidEvaluation(evaluation, session) {
  if (!session || session.role === 'super-admin' || !session.userId) return false;
  return EDIT_ROLES.has(session.role) || evaluation?.evaluatedBy === session.userId;
}

/** The cycle state of one enabled obligation now, or null when it is not enabled. */
function evaluationStatusFor(state, obligationId, now = Date.now()) {
  const def = OBLIGATIONS_BY_ID.get(obligationId);
  const entry = (state?.enabledObligations || []).find(
    (e) => e && e.obligationId === obligationId && e.enabled !== false
  );
  if (!def || !entry) return null;
  return obligationCycleState({
    state,
    def,
    entry,
    atMs: now,
    findingsKnown: Array.isArray(state?.findings),
  });
}

/**
 * Tier 1 tasks for evaluations due now (due soon or overdue).
 *
 * @param {object} state
 * @param {object[]} obligationTasks  the evaluator's tasks (any status), read
 *   only for the obligation's controls and responsibility — the same answer
 *   the obligation's own task carries (COP-B-004)
 * @param {number} [now]
 */
function buildEvaluationTasks(state, obligationTasks = [], now = Date.now()) {
  const byObligation = new Map((obligationTasks || []).map((t) => [t.obligationId, t]));
  const projections = state?.obligationProjections || [];
  const out = [];
  const seen = new Set();
  for (const entry of state?.enabledObligations || []) {
    if (!entry || entry.enabled === false || seen.has(entry.obligationId)) continue;
    seen.add(entry.obligationId);
    const def = OBLIGATIONS_BY_ID.get(entry.obligationId);
    if (!def) continue;
    const st = obligationCycleState({
      state,
      def,
      entry,
      atMs: now,
      findingsKnown: Array.isArray(state?.findings),
    });
    if (st.state !== 'due' && st.state !== 'overdue') continue;
    const base = byObligation.get(def.id) || {};
    const proj = projections.find((p) => p && p.obligationId === def.id);
    const assigneeId = proj?.assigneeId || entry.assigneeId || null;
    const responsibleEmployeeIds = base.responsibleEmployeeIds ?? null;
    out.push({
      id: `obl-eval-${def.id}`,
      kind: EVALUATION_TASK_KIND,
      obligationId: def.id,
      title: `Evaluate: ${def.title}`,
      description:
        'Record a person’s evaluation of this obligation: whether anything must change, and the evidence it rests on. The automatic compliance check does not count as an evaluation.',
      controlIds: base.controlIds || [],
      status: st.state === 'overdue' ? 'overdue' : 'unmet',
      dueDate: new Date(st.dueMs).toISOString(),
      fulfilmentState: [],
      domainActions: [],
      evidenceRefs: [],
      assigneeId,
      responsibleEmployeeIds,
      // Derived and nobody owes it — an ownership gap, never a quiet task.
      ownershipGap:
        !assigneeId && Array.isArray(responsibleEmployeeIds) && responsibleEmployeeIds.length === 0,
      snoozedUntil: null,
    });
  }
  return out;
}

module.exports = {
  OBLIGATION_EVALUATION_FROM,
  OUTCOMES,
  obligationCycleState,
  evaluationsOf,
  creditRefusal,
  findingFor,
  evidenceResolves,
  isVoided,
  EVALUATION_TASK_KIND,
  OUTCOME_LABELS,
  REFUSAL_LABELS,
  mayEvaluateObligation,
  mayVoidEvaluation,
  evaluationStatusFor,
  buildEvaluationTasks,
};
